import { motion } from "framer-motion";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { CalendarCheck, Bell, Wallet, CheckCircle2, ArrowRight } from "lucide-react";
import { Logo } from "@/components/Logo";
import { affiliateApi } from "@/lib/agenda";

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.11, delayChildren: 0.05 } },
};
const item = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};

const FEATURES = [
  { icon: CalendarCheck, text: "Ves tus citas del día en un vistazo" },
  { icon: Bell, text: "Programas el recordatorio al guardar la cita" },
  { icon: Wallet, text: "Registras notas y el pago tras la sesión" },
];

export const Landing = ({ onStart }) => {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    if (ref) {
      localStorage.setItem("affiliate_ref", ref);
      affiliateApi.trackReferral(ref).catch(() => {});
    }
  }, []);

  return (
    <div className="min-h-screen flex flex-col warm-canvas" data-testid="landing-page">
      <main className="flex-1 grid lg:grid-cols-2 items-center gap-10 px-6 sm:px-12 lg:px-16 py-14">
        <motion.div
          variants={container}
          initial="hidden"
          animate="show"
          className="max-w-xl mx-auto w-full flex flex-col items-center text-center"
        >
          <motion.span
            variants={item}
            className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400 mb-6"
          >
            Agenda para terapeutas
          </motion.span>

          <motion.div variants={item}>
            <Logo stacked center />
          </motion.div>

          <motion.p variants={item} className="text-base text-slate-600 leading-relaxed mt-7 max-w-md">
            Hola, estás en tu nueva agenda. Aquí todo está en un solo panel.
            Organiza tus citas y clientes sin esfuerzo ni olvidos.
          </motion.p>

          <motion.div variants={item} className="mt-8 w-full max-w-sm space-y-2.5">
            {FEATURES.map((f) => (
              <div
                key={f.text}
                className="flex items-center gap-3 text-left bg-white/70 border border-[#EEE8E1] rounded-xl px-4 py-3"
              >
                <span
                  className="flex items-center justify-center h-9 w-9 rounded-full shrink-0"
                  style={{ backgroundColor: "#F0F5FF", color: "#275FBE" }}
                >
                  <f.icon className="h-4.5 w-4.5" />
                </span>
                <span className="text-sm text-slate-700">{f.text}</span>
              </div>
            ))}
          </motion.div>

          <motion.div variants={item} className="mt-8">
            <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
              <Button
                onClick={onStart}
                className="rounded-full px-8 py-6 text-base text-white shadow-lg shadow-[#275FBE]/20 gap-2 group"
                style={{ backgroundColor: "#275FBE" }}
                data-testid="start-button"
              >
                Empezar ahora
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Button>
            </motion.div>
          </motion.div>

          <motion.p variants={item} className="text-sm text-slate-500 mt-6 max-w-sm">
            Evitas no‑shows con recordatorios automáticos. Si evitas una sola
            cita perdida, ya está pagado.
          </motion.p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
          className="hidden lg:block relative"
        >
          <div className="relative">
            <div
              className="absolute -inset-4 rounded-[2rem] -rotate-2"
              style={{ backgroundColor: "rgba(46,42,143,0.07)" }}
            />
            <div className="relative rounded-[1.75rem] overflow-hidden border border-white shadow-[0_30px_60px_-20px_rgba(46,42,143,0.35)]">
              <img
                src="https://images.unsplash.com/photo-1714976694756-28bf07af3758?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHx0aGVyYXBpc3QlMjBzZXNzaW9uJTIwY29uc3VsdGF0aW9uJTIwY2FsbSUyMGludGVyaW9yJTIwd2FybSUyMGxpZ2h0fGVufDB8fHx8MTc5MDk2MTQwN3ww&ixlib=rb-4.1.0&q=85&w=1100"
                alt="Consulta tranquila"
                className="w-full h-[560px] object-cover"
              />
              <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(46,42,143,0) 55%, rgba(46,42,143,0.25) 100%)" }} />
            </div>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="absolute -left-6 bottom-10 editorial-card p-4 w-64"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-heading text-base font-semibold text-slate-900 leading-none">María Gómez</p>
                  <p className="text-xs text-slate-500 mt-1">Hoy · 10:00</p>
                </div>
                <span className="text-slate-900 font-heading font-bold">55 €</span>
              </div>
              <div className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-50 border border-emerald-200 text-emerald-700">
                <CheckCircle2 className="h-3.5 w-3.5" />
                Recordatorio enviado
              </div>
            </motion.div>
          </div>
        </motion.div>
      </main>

      <footer className="px-6 sm:px-12 lg:px-16 py-5 border-t border-[#EDE7E0] text-sm text-slate-500">
        Agenda Terapias · Tu trabajo, más claro y sin olvidos.
      </footer>
    </div>
  );
};
